import { NEW_CONVERSATION_ID, PRERENDERED_CONVO_ID } from 'constants/conversations'
import { getMessageCreatedAt } from 'modules/message/utils'
import { isEmpty, last, map, merge, path, pathOr, uniq } from 'ramda'
import { ConversationId, ConversationSummary } from 'types/conversation'
import { Message, MessageAuthorType, MessageContentType, ViewerRecipientStatus } from 'types/message'
import { byCampaignId, byId,isNilOrEmpty } from 'utils'
import { filterUnwantedMessageTypes, messagesByWidgetGuid } from 'utils/conversation'
import { createReducer } from 'utils/redux'

import { getConvoIdFromMessage } from './utils'


const defaultState = {
  endUserEngaged: false,
  activeConversation: null,
  activeDriftlink: null,
  attachments: {},
  conversations: {},
  conversationStatus: {},
  typingIndicators: {},
  agentMessagesFromCurrentSession: {},
  messages: {}, //convoId -> messages,
  unreadAgentMessages: {}, //convoId -> messages,
  agentPresence: {},
  resumeFlowContext: {},
  hasForcedConversation: false,
  campaignConversations: {},
  ignoredConversations: []
}

const mergeConversationStatus = (draft, conversationId, nextStatus: Record<string, any>) => {
  draft.conversationStatus[conversationId] = {
    ...draft.conversationStatus[conversationId],
    ...nextStatus
  }
}

const receiveInitialActiveConversationHandler = ({ action, draft }) => {
  draft.activeConversation = action.payload.conversation.conversation.id
  draft.conversations[action.payload.conversation.conversation.id] = action.payload.conversation
}

const setIsTypingHandler = ({ action, draft }) => {
  draft.typingIndicators[action.payload.conversationId] = action.payload
}

const receiveAttachments = ({ action, draft }) => {
  draft.attachments = {
    ...draft.attachments,
    ...byId(action.payload.attachments)
  }
}

const setActiveConversationHandler = ({ action, draft }) => {
  draft.activeConversation = action.payload.conversationId
}

const clearActiveConversationHandler = ({ draft }) => {
  draft.activeConversation = null
}

const sendMessageHandler = ({ action, draft }) => {

  let conversationId: ConversationId = getConvoIdFromMessage(action.payload.message)
  draft.activeConversation = conversationId
  draft.chatOpen = true

  draft.messages[conversationId] = {
    ...(draft.messages[conversationId] || {}),
    [action.payload.message.attributes.widgetGuid]: action.payload.message
  }

  if(!draft.conversations[conversationId]) {
    draft.conversations[conversationId] = { conversation: { id: conversationId }}
  }

  updateLastMessage(draft, action.payload.message)
}

/**
 * Updates the last message in the conversation object for that conversation id
 * In the case that the latest message was createdAt before the message prior, don't set it as the last message
 * This prevents us from displaying messages out of order even if the websocket sends messages to us that way
 * @param draft - redux store draft
 * @param message - message being received by the reducer
 */
const updateLastMessage = (draft, message: Message) => {
  if (draft.conversations[message.conversationId]) {
    const lastMessage: Message = draft.conversations[message.conversationId].lastMessage

    if (!!lastMessage && getMessageCreatedAt(message) <= getMessageCreatedAt(lastMessage)) {
      return
    }

    draft.conversations[message.conversationId].lastMessage = message
  }
}

const receiveMessageSendFailureHandler = ({action,draft}) => {
  const { payload } = action
  const message : Message = payload?.message

  let conversationId = getConvoIdFromMessage(message)

  const modified = map((msg:Message)=>{
    return msg.id !== message.id ? msg :
    {
      ...msg,
      failedToSend: true
    }

  },draft.messages[conversationId])

  draft.messages[conversationId] = modified
}

const receiveMessageSentHandler = ({ action, draft }) => {
  const { payload } = action
  const { message, isNew = false, isPreRendered = false } = payload

  if(isNew) {
    // Special logic to handle pre-rendered messaged - don't re-create pre-rendered messages to avoid rendering jank

    const newConversationId = isPreRendered ? PRERENDERED_CONVO_ID : NEW_CONVERSATION_ID
    draft.messages[message.conversationId] = {
      ...draft.messages[newConversationId],
      [message.attributes.widgetGuid]: message
    }

    // we create a minimal placeholder conversation with critical data to help with
    // rendering and drift.page() functionality (convo needs and ID and createdAt)
    draft.conversations[message.conversationId] = {
      ...draft.conversations[newConversationId],
      conversation: {
        ...draft.conversations[newConversationId].conversation,
        id: message.conversationId,
        createdAt: message.createdAt,
        updatedAt: message.createdAt
      }
    }

    // delete placeholder prerendered convo/messages
    delete draft.conversations[newConversationId]
    delete draft.messages[newConversationId]
  }

  draft.endUserEngaged = true
  if ((!isNew || isPreRendered) && !isEmpty(message.body)) {
    mergeConversationStatus(draft, message.conversationId, { endUserResponded: true })
  }

  draft.messages[message.conversationId] = {
    ...draft.messages[message.conversationId],
    [message.attributes.widgetGuid]: message
  }

  draft.activeConversation = message.conversationId

  if(!draft.conversations[message.conversationId]) {
    draft.conversations[message.conversationId] = { conversation: { id: message.conversationId }}
  }

  updateLastMessage(draft, message)
}

const receiveMessagesForConversationHandler = ({ action, draft }) => {
  const { payload } = action
  const { messages, conversationId, hasMoreMessages = false, nextPageMessageId = null } = payload

  const existingMessages = pathOr({}, ['messages', conversationId], draft)

  draft.messages[conversationId] = {
    ...messagesByWidgetGuid(Object.values(existingMessages)),
    ...messagesByWidgetGuid(
      filterUnwantedMessageTypes(messages, [MessageContentType.PRIVATE_NOTE])
    )
  }

  draft.conversations[conversationId] = {
    ...draft.conversations[conversationId],
    hasMoreMessages,
    nextPageMessageId,
  }

  const unreadAgentMessages = (message) => message.viewerRecipientStatus !==  ViewerRecipientStatus.READ
    && message.authorType === MessageAuthorType.USER
    && (!!message.body && !!message.body.length)

  const unread = messages
    .filter(unreadAgentMessages)
    .map((message) => message.id)
  draft.unreadAgentMessages[conversationId] = unread

  const hasNonEmptyEndUserMessages = (message: Message) => (
    (message.authorType === MessageAuthorType.END_USER || message.authorType === MessageAuthorType.LEAD) &&
    !!message.body && !isEmpty(message.body)
  )
  if (messages.filter(hasNonEmptyEndUserMessages).length > 0) {
    mergeConversationStatus(draft, conversationId, { endUserResponded: true })
  }
}

const receiveAgentMessageHandler = ({ action, draft }) => {
  const { payload = {} } = action
  const { message } = payload

  if (!message || !message.conversationId) {
    return
  }

  draft.messages[message.conversationId] = {
    ...draft.messages[message.conversationId],
    [message.id]: message
  }

  updateLastMessage(draft, message)
}

const receivePreRenderedMessagesHandler = ({ action, draft }) => {
  const { payload } = action
  const { messages, resumeFlowContext } = payload

  if (!messages || !messages.length) {
    return
  }

  // Because all of the messages that come back are id: 0, we increment the id's in local state
  // so we can mark as read nicely until the conversation is created
  const mappedMessages = messages.map((msg, index) => {
    return {
      ...msg,
      id: index
    }
  })

  draft.messages[PRERENDERED_CONVO_ID] = mappedMessages
  draft.unreadAgentMessages[PRERENDERED_CONVO_ID] = mappedMessages.map(msg => msg.id)
  draft.conversations[PRERENDERED_CONVO_ID] = {
    conversation: {
      id: PRERENDERED_CONVO_ID,
      updatedAt: Date.now()
    },
    lastMessage: last(mappedMessages),
    lastAgentMessage: last(mappedMessages),
    unreadMessages: mappedMessages.length
  }

  draft.resumeFlowContext = resumeFlowContext
  draft.activeConversation = PRERENDERED_CONVO_ID
}

const receiveConversationHandler = ({ action, draft }) => {
  const { payload } = action
  const { conversation } = payload
  draft.conversations[conversation.conversation.id] = conversation
}

const receiveBulkConversationsHandler = ({ action, draft }) => {
  const { payload } = action
  const { conversations } = payload

  const conversationSummariesById: Record<ConversationId, ConversationSummary> = conversations.reduce((acc, item) => {
    acc[item.conversation.id] = item
    return acc
  }, {})

  draft.conversations = merge(
    draft.conversations,
    conversationSummariesById
  )
}

const sendReadReceiptHandler = ({ action, draft }) => {
  const { payload } = action
  const { conversationId } = payload
  draft.unreadAgentMessages[conversationId] = []
}

const setUnreadMessageHandler = ({action, draft}) => {
  const { payload } = action
  const { message } = payload

  if (!draft.unreadAgentMessages[message.conversationId]) {
    draft.unreadAgentMessages[message.conversationId] = [message.id]
  } else {
    draft.unreadAgentMessages[message.conversationId].push(message.id)
  }
}

const setActiveDriftlinkHandler = ({ action, draft }) => {
  draft.activeDriftlink = action.payload
}

const setHasForcedConversationHandler = ({ draft }) => {
  draft.hasForcedConversation = true
}

const receiveAgentStatusHandler = ({ action, draft }) => {
  const { agentId, availability, lastActivity } = action.payload

  draft.agentPresence[agentId] = {
    status: availability,
    lastActivity
  }
}

const permacloseConversation = ({ action, draft }) => {
  const { conversationId } = action.payload
  mergeConversationStatus(draft, conversationId, { permaclosed: true })
}

const receiveCampaignConversationsHandler = ({ action, draft }) => {
  const campaignConversations = action.payload

  if (isNilOrEmpty(campaignConversations)) {
    return
  }

  const campaignConversationsByCampaignId =  byCampaignId(map(path(['conversation']), action.payload))
  draft.campaignConversations = campaignConversationsByCampaignId
}

const setIgnoredConversation = ({action, draft}) => {
  const newIgnoredConvos = Array.isArray(action.payload) ? action.payload : [action.payload]
  draft.ignoredConversations = uniq([
    ...draft.ignoredConversations,
    ...newIgnoredConvos
  ])
}

const actions = {
  SEND_MESSAGE: sendMessageHandler,
  RECEIVE_MESSAGE_SENT: receiveMessageSentHandler,
  RECEIVE_PRE_RENDERED_MESSAGES: receivePreRenderedMessagesHandler,
  RECEIVE_AGENT_MESSAGE: receiveAgentMessageHandler,
  RECEIVE_CONVERSATION: receiveConversationHandler,
  RECEIVE_INITIAL_ACTIVE_CONVERSATION: receiveInitialActiveConversationHandler,
  RECEIVE_MESSAGE_SEND_FAILURE: receiveMessageSendFailureHandler,
  RECEIVE_MESSAGES_FOR_CONVERSATION: receiveMessagesForConversationHandler,
  RECEIVE_BULK_CONVERSATIONS: receiveBulkConversationsHandler,
  SET_ACTIVE_CONVERSATION: setActiveConversationHandler,
  CLEAR_ACTIVE_CONVERSATION: clearActiveConversationHandler,
  RECEIVE_READ_MESSAGES: sendReadReceiptHandler,
  SET_UNREAD_MESSAGE: setUnreadMessageHandler,
  RECEIVE_ATTACHMENTS: receiveAttachments,
  RECEIVE_IS_TYPING: setIsTypingHandler,
  SET_ACTIVE_DRIFTLINK: setActiveDriftlinkHandler,
  RECEIVE_AGENT_STATUS: receiveAgentStatusHandler,
  PERMACLOSE_CONVERSATION: permacloseConversation,
  SET_HAS_FORCED_CONVERSATION: setHasForcedConversationHandler,
  RECEIVE_CAMPAIGN_CONVERSATIONS: receiveCampaignConversationsHandler,
  SET_IGNORED_CONVERSATION: setIgnoredConversation
}

const conversationsReducer = createReducer(
  defaultState,
  actions
)

export default conversationsReducer
